import React, { useState, useEffect } from 'react';
import { X, TrendingUp, DollarSign, AlertCircle, Info } from 'lucide-react';
import { usePOCManagement } from '@/hooks/admin/usePOCManagement';

export function CreateAmplifyPOCDialog({ isOpen, onClose, onSuccess }) {
  const { addAmplifyPOC, checkExists } = usePOCManagement();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isChecking, setIsChecking] = useState(false);
  const [error, setError] = useState(null);
  const [pocExists, setPocExists] = useState(null);
  
  const [formData, setFormData] = useState({
    id: '',
    poc_name: '',
    country: 'Canada',
    region: '',
    city: '',
    traditional_budget: 0,
    add_to_poc_table: false
  });

  // Check if POC exists when ID changes
  useEffect(() => {
    const checkPOCExists = async () => {
      if (formData.id && formData.id.length > 0) {
        setIsChecking(true);
        try {
          const result = await checkExists(formData.id);
          setPocExists(result);
          
          // Auto-check the "add to POC table" if POC doesn't exist
          if (!result?.exists_in_poc) {
            setFormData(prev => ({ ...prev, add_to_poc_table: true }));
          }
        } catch (err) {
          console.error('Error checking POC:', err);
          setPocExists(null);
        } finally {
          setIsChecking(false);
        }
      } else {
        setPocExists(null);
      }
    };

    const timeoutId = setTimeout(checkPOCExists, 500);
    return () => clearTimeout(timeoutId);
  }, [formData.id, checkExists]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.id || !formData.poc_name || !formData.country) {
      setError('POC ID, Name, and Country are required');
      return;
    }

    // Validate if POC exists in main table when add_to_poc_table is false
    if (!formData.add_to_poc_table && pocExists && !pocExists.exists_in_poc) {
      setError(`POC ID "${formData.id}" does not exist in the POC table. Please check "Also add to POC table" to create it.`);
      return;
    }

    setIsSubmitting(true);
    setError(null);
    try {
      await addAmplifyPOC(formData);
      onSuccess();
    } catch (err) {
      // Provide user-friendly error message
      const errorMessage = err.message || 'Failed to create Amplify POC';
      if (errorMessage.includes('does not exist in POC table')) {
        setError(`POC ID "${formData.id}" does not exist in the POC table. Please check "Also add to POC table" to create it.`);
      } else {
        setError(errorMessage);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white dark:bg-gray-900 rounded-lg shadow-xl max-w-2xl w-full m-4">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
            <TrendingUp className="h-6 w-6 text-gray-600 dark:text-gray-400" />
            Create Amplify POC
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"><X className="h-6 w-6" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-800 dark:text-red-200">{error}</span>
            </div>
          )}

          {/* POC Existence Warning */}
          {pocExists && !pocExists.exists_in_poc && formData.id && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 flex items-start gap-2">
              <Info className="h-5 w-5 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-yellow-800 dark:text-yellow-200">
                <strong>POC ID "{formData.id}" not found in POC table.</strong>
                <p className="mt-1">Check "Also add to POC table" below to create it automatically, or create it in the POC table first.</p>
              </div>
            </div>
          )}

          {/* POC Already Exists in Amplify Warning */}
          {pocExists && pocExists.exists_in_amplify && formData.id && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 flex items-start gap-2">
              <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-blue-800 dark:text-blue-200">
                POC ID "{formData.id}" already exists in Amplify POC table. Creating it again will update the existing record.
              </span>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">
                POC ID * {isChecking && <span className="text-xs text-gray-500">(checking...)</span>}
              </label>
              <input 
                type="text" 
                value={formData.id} 
                onChange={(e) => {
                  setFormData({...formData, id: e.target.value});
                  setError(null);
                }} 
                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white" 
                required 
                placeholder="e.g., 7349821"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">POC Name *</label>
              <input type="text" value={formData.poc_name} onChange={(e) => setFormData({...formData, poc_name: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white" required />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">Country *</label>
              <input type="text" value={formData.country} onChange={(e) => setFormData({...formData, country: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white" required />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">Region</label>
              <input type="text" value={formData.region} onChange={(e) => setFormData({...formData, region: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 dark:text-gray-300">City</label>
              <input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
            </div>
          </div>

          <div>
            <label className="flex items-center gap-1 text-sm font-medium mb-1 dark:text-gray-300">
              <DollarSign className="h-4 w-4" />
              Traditional Budget
            </label>
            <input type="number" step="0.01" value={formData.traditional_budget} onChange={(e) => setFormData({...formData, traditional_budget: parseFloat(e.target.value) || 0})} className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-600 dark:text-white" />
          </div>

          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <input 
                type="checkbox" 
                id="add_to_poc_table"
                checked={formData.add_to_poc_table} 
                onChange={(e) => {
                  setFormData({...formData, add_to_poc_table: e.target.checked});
                  setError(null);
                }} 
                className="h-4 w-4 text-gray-600 rounded mt-0.5" 
              />
              <div className="flex-1">
                <label htmlFor="add_to_poc_table" className="text-sm font-medium text-gray-900 dark:text-white cursor-pointer">
                  Also add to POC table
                </label>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  If checked, this will create the POC in both the main POC table and Amplify POC table. 
                  {pocExists && !pocExists.exists_in_poc && (
                    <span className="text-yellow-600 dark:text-yellow-400 font-medium"> (Recommended - POC doesn't exist yet)</span>
                  )}
                </p>
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4 border-t dark:border-gray-700">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">Cancel</button>
            <button type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-medium text-white bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 dark:hover:bg-gray-600 rounded-lg disabled:opacity-50">{isSubmitting ? 'Creating...' : 'Create'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default CreateAmplifyPOCDialog;
